<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SegmentedToggle from 'dashboard/components-next/segmented-toggle/SegmentedToggle.vue';
import {
  extractDocumentTitle,
  formatContent,
} from 'dashboard/utils/documentHelpers';

const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  knowledgebaseId: {
    type: Number,
    required: false,
    default: null,
  },
});

const emit = defineEmits(['close', 'select']);

// Store and I18n
const store = useStore();
const { t } = useI18n();

// Template refs
const searchInputRef = ref(null);
const resultsRef = ref(null);

// State
const searchQuery = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
const filterApplied = ref({});
const queryInfo = ref({});
const searchStrategy = ref('vsearch');
const currentSearchQuery = ref(''); // Track what we're currently searching for
const travelMode = ref('walking');
const maxTravelTime = ref(30);

// Computed
const hasResults = computed(() => searchResults.value.length > 0);
const hasFiltersApplied = computed(() => {
  if (!filterApplied.value) return false;

  const hasOptionalConditions =
    filterApplied.value.optional_conditions &&
    filterApplied.value.optional_conditions.length > 0;

  const hasRequiredConditions =
    filterApplied.value.required_conditions &&
    filterApplied.value.required_conditions.length > 0;

  return hasOptionalConditions || hasRequiredConditions;
});

const searchStrategyOptions = computed(() => [
  {
    value: 'vsearch',
    label: t('LIBRARIES.SEARCH.STRATEGY.SEMANTIC'),
    icon: 'i-lucide-brain',
  },
  {
    value: 'rsearch',
    label: t('LIBRARIES.SEARCH.STRATEGY.RETRIEVAL'),
    icon: 'i-lucide-filter',
  },
  {
    value: 'ksearch',
    label: t('LIBRARIES.SEARCH.STRATEGY.KEYWORD'),
    icon: 'i-lucide-type',
  },
  {
    value: 'hsearch',
    label: t('LIBRARIES.SEARCH.STRATEGY.HYBRID'),
    icon: 'i-lucide-zap',
  },
  {
    value: 'nearby',
    label: t('LIBRARIES.SEARCH.STRATEGY.NEARBY'),
    icon: 'i-lucide-map-pin',
  },
]);

const handleBackdropClick = () => {
  emit('close');
};

const selectDocument = result => {
  if (result.metadata?.reference) {
    window.open(result.metadata.reference, '_blank', 'noopener,noreferrer');
  }

  emit('select', result);
};

const performSearch = async query => {
  const trimmedQuery = query.trim();

  // Clear results if query is empty
  if (!trimmedQuery) {
    searchResults.value = [];
    isSearching.value = false;
    filterApplied.value = {};
    queryInfo.value = {};
    currentSearchQuery.value = '';
    return;
  }

  // Check if knowledgebase is available
  if (!props.knowledgebaseId) {
    searchResults.value = [];
    isSearching.value = false;
    return;
  }

  // Don't search the same query if already searching
  if (isSearching.value && trimmedQuery === currentSearchQuery.value) {
    return;
  }

  // Cancel any existing search for non-nearby strategies
  if (searchStrategy.value !== 'nearby') {
    store.dispatch('knowledgebase/cancelActiveSearch');
  }

  // Set search state
  isSearching.value = true;
  currentSearchQuery.value = trimmedQuery;
  filterApplied.value = {};
  queryInfo.value = {};

  try {
    let documents = [];

    if (searchStrategy.value === 'nearby') {
      const result = await store.dispatch('knowledgebase/nearbySearchAction', {
        id: props.knowledgebaseId,
        query: trimmedQuery,
        travel_mode: travelMode.value,
        max_travel_time_minutes: Math.min(
          Math.max(Number(maxTravelTime.value), 1),
          60
        ),
        max_results: 19,
      });
      documents = result.documents || [];
      queryInfo.value = result.query_info || {};
    } else {
      const result = await store.dispatch(
        'knowledgebase/searchDocumentsAction',
        {
          id: props.knowledgebaseId,
          query: trimmedQuery,
          searchType: searchStrategy.value,
        }
      );
      documents = result.documents || result || [];
      filterApplied.value = result.filter_applied || {};
      queryInfo.value = result.query_info || {};
    }

    // Transform API response
    searchResults.value = documents.map(document => ({
      id: document.id || Math.random().toString(36).substr(2, 9),
      content: document.page_content,
      metadata: {
        title: extractDocumentTitle({
          metadata: document.metadata,
          content: document.page_content,
        }),
        ...document.metadata,
      },
    }));
  } catch (error) {
    if (error.name !== 'AbortError') {
      // eslint-disable-next-line no-console
      console.error('Search failed:', error);
      searchResults.value = [];
    }
  } finally {
    // Only clear loading if this query is still current
    if (currentSearchQuery.value === trimmedQuery) {
      isSearching.value = false;
    }
  }
};

const handleKeydown = event => {
  switch (event.key) {
    case 'Enter':
      event.preventDefault();
      if (searchQuery.value.trim()) {
        performSearch(searchQuery.value);
      }
      break;
    case 'Escape':
      emit('close');
      break;
    default:
      break;
  }
};

// Utility functions
const highlightSearchTerms = (text, searchTerm) => {
  if (!searchTerm || !text) return text;

  const regex = new RegExp(`(${searchTerm})`, 'gi');
  return text.replace(
    regex,
    '<mark class="bg-yellow-200 dark:bg-yellow-700">$1</mark>'
  );
};

// Focus search input when dialog opens
watch(
  () => props.show,
  newShow => {
    if (newShow) {
      nextTick(() => {
        searchInputRef.value?.focus();
      });
    } else {
      // Clear search state when closing
      searchQuery.value = '';
      searchResults.value = [];
      isSearching.value = false;
      filterApplied.value = {};
      queryInfo.value = {};
      searchStrategy.value = 'vsearch';
      currentSearchQuery.value = '';
      travelMode.value = 'walking';
      maxTravelTime.value = 30;

      // Cancel any active search in the store
      store.dispatch('knowledgebase/cancelActiveSearch');
    }
  }
);

// Remove auto-search - search only triggered manually

// Watch search strategy - re-search when strategy changes
watch(searchStrategy, () => {
  if (searchQuery.value.trim()) {
    performSearch(searchQuery.value);
  } else {
    // Ensure loading state is cleared if there's no query
    isSearching.value = false;
  }
});

// This watcher is now redundant since we handle state clearing in the main show watcher above

// Force search trigger removed - search is now manual only
</script>

<template>
  <teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 backdrop-blur-sm"
      @click="handleBackdropClick"
    >
      <div
        class="flex items-start justify-center min-h-screen pt-12 sm:pt-20 px-4 ml-[16rem]"
      >
        <div
          class="relative w-full max-w-sm sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl bg-white dark:bg-slate-800 rounded-lg shadow-xl"
          @click.stop
        >
          <!-- Search Input -->
          <div class="p-4 border-b border-slate-200 dark:border-slate-700">
            <div class="flex gap-2">
              <input
                ref="searchInputRef"
                v-model="searchQuery"
                type="text"
                :placeholder="t('LIBRARIES.SEARCH.PLACEHOLDER')"
                class="no-margin flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                @keydown="handleKeydown"
              />
              <Button
                :label="t('LIBRARIES.SEARCH.BUTTON')"
                icon="i-lucide-search"
                :disabled="!searchQuery.trim() || isSearching"
                :is-loading="isSearching"
                @click="performSearch(searchQuery)"
              />
            </div>
          </div>

          <!-- Search Strategy Toggle -->
          <div
            class="px-4 py-3 border-b border-slate-200 dark:border-slate-700"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <Icon
                  icon="i-lucide-settings"
                  class="h-4 w-4 text-slate-500 dark:text-slate-400"
                />
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  {{ t('LIBRARIES.SEARCH.STRATEGY.LABEL') }}
                </span>
              </div>
              <SegmentedToggle
                v-model="searchStrategy"
                :options="searchStrategyOptions"
                size="small"
                class="shrink-0"
              />
            </div>
          </div>

          <!-- Nearby Controls (only when nearby strategy is selected) -->
          <div
            v-if="searchStrategy === 'nearby'"
            class="px-4 py-3 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50"
          >
            <div class="flex items-center gap-4 flex-wrap">
              <div class="flex items-center gap-2">
                <span
                  class="text-xs font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap"
                >
                  {{ t('LIBRARIES.NEARBY.TRAVEL_MODE') }}
                </span>
                <select
                  v-model="travelMode"
                  class="reset-base text-xs px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100"
                >
                  <option value="walking">
                    {{ t('LIBRARIES.NEARBY.TRAVEL_MODES.WALKING') }}
                  </option>
                  <option value="cycling">
                    {{ t('LIBRARIES.NEARBY.TRAVEL_MODES.CYCLING') }}
                  </option>
                  <option value="driving">
                    {{ t('LIBRARIES.NEARBY.TRAVEL_MODES.DRIVING') }}
                  </option>
                  <option value="driving-traffic">
                    {{ t('LIBRARIES.NEARBY.TRAVEL_MODES.DRIVING_TRAFFIC') }}
                  </option>
                  <option value="public_transport">
                    {{ t('LIBRARIES.NEARBY.TRAVEL_MODES.PUBLIC_TRANSPORT') }}
                  </option>
                </select>
              </div>
              <div class="flex items-center gap-2">
                <span
                  class="text-xs font-medium text-slate-600 dark:text-slate-400 whitespace-nowrap"
                >
                  {{ t('LIBRARIES.NEARBY.MAX_TRAVEL_TIME') }}
                </span>
                <input
                  v-model="maxTravelTime"
                  type="number"
                  min="1"
                  max="60"
                  class="reset-base w-16 text-xs px-2 py-1 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100"
                />
              </div>
              <span
                class="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
              >
                {{ t('LIBRARIES.NEARBY.BETA') }}
              </span>
            </div>
          </div>

          <!-- Query Info Display -->
          <div
            v-if="queryInfo.hasOwnProperty('cleaned_query')"
            class="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-amber-50 dark:bg-amber-900/20"
          >
            <div class="flex items-center gap-2 mb-1">
              <Icon
                icon="i-lucide-sparkles"
                class="h-4 w-4 text-amber-600 dark:text-amber-400"
              />
              <span
                class="text-sm font-medium text-amber-800 dark:text-amber-200"
              >
                {{ t('LIBRARIES.SEARCH.CLEANED_QUERY') }}
              </span>
            </div>
            <div class="text-sm text-amber-700 dark:text-amber-300">
              <span v-if="queryInfo.cleaned_query">
                {{ `"${queryInfo.cleaned_query}"` }}
              </span>
              <span v-else class="italic text-amber-600 dark:text-amber-400">
                {{ t('LIBRARIES.SEARCH.EMPTY_QUERY') }}
              </span>
            </div>
          </div>

          <!-- Nearby Origin Info -->
          <div
            v-if="queryInfo.origin"
            class="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-green-50 dark:bg-green-900/20"
          >
            <div class="flex items-center gap-2 mb-1">
              <Icon
                icon="i-lucide-map-pin"
                class="h-4 w-4 text-green-600 dark:text-green-400"
              />
              <span
                class="text-sm font-medium text-green-800 dark:text-green-200"
              >
                {{
                  queryInfo.origin_geocoded
                    ? t('LIBRARIES.NEARBY.ORIGIN_GEOCODED')
                    : t('LIBRARIES.NEARBY.ORIGIN_LABEL')
                }}
              </span>
            </div>
            <div
              class="flex items-center gap-3 text-xs text-green-700 dark:text-green-300"
            >
              <span>
                {{ queryInfo.origin.lat?.toFixed(4) }},
                {{ queryInfo.origin.lon?.toFixed(4) }}
              </span>
              <span v-if="queryInfo.radius_m">
                &bull;
                {{
                  t('LIBRARIES.NEARBY.RADIUS_M', {
                    radius: Math.round(queryInfo.radius_m),
                  })
                }}
              </span>
              <span v-if="queryInfo.isochrone_used !== undefined">
                &bull;
                {{
                  queryInfo.isochrone_used
                    ? t('LIBRARIES.NEARBY.ISOCHRONE_USED')
                    : t('LIBRARIES.NEARBY.RADIUS_APPROX')
                }}
              </span>
            </div>
          </div>

          <!-- Applied Filters Display -->
          <div
            v-if="hasFiltersApplied"
            class="px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-blue-50 dark:bg-blue-900/20"
          >
            <div class="flex items-center gap-2 mb-2">
              <Icon
                icon="i-lucide-filter"
                class="h-4 w-4 text-blue-600 dark:text-blue-400"
              />
              <span
                class="text-sm font-medium text-blue-800 dark:text-blue-200"
              >
                {{ t('LIBRARIES.SEARCH.FILTERS_APPLIED') }}
              </span>
            </div>
            <div class="flex flex-wrap gap-1">
              <!-- Optional Conditions -->
              <span
                v-for="(
                  condition, index
                ) in filterApplied.optional_conditions || []"
                :key="`optional-${index}`"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200"
              >
                {{ `${condition.field}: ${condition.condition}` }}
              </span>
              <!-- Required Conditions -->
              <span
                v-for="(
                  condition, index
                ) in filterApplied.required_conditions || []"
                :key="`required-${index}`"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-200"
              >
                {{ `${condition.field}: ${condition.condition}` }}
              </span>
            </div>
          </div>

          <!-- Results Area -->
          <div
            class="flex-1 overflow-y-auto max-h-60 sm:max-h-80 md:max-h-96 lg:max-h-[32rem] xl:max-h-[36rem]"
          >
            <!-- Knowledgebase not loaded state -->
            <div
              v-if="!knowledgebaseId"
              class="flex flex-col items-center justify-center py-8 text-slate-500 dark:text-slate-400"
            >
              <Icon icon="i-lucide-clock" class="h-12 w-12 mb-3 opacity-50" />
              <p class="text-sm">{{ t('LIBRARIES.SEARCH.LOADING') }}</p>
            </div>

            <!-- Loading state -->
            <div
              v-else-if="isSearching"
              class="flex items-center justify-center py-8"
            >
              <div
                class="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900 dark:border-slate-100"
              />
              <span class="ml-2 text-sm text-slate-600 dark:text-slate-400">{{
                t('LIBRARIES.SEARCH.SEARCHING')
              }}</span>
            </div>

            <!-- Empty state -->
            <div
              v-else-if="!searchQuery.trim()"
              class="flex flex-col items-center justify-center py-8 text-slate-500 dark:text-slate-400"
            >
              <Icon
                icon="i-lucide-file-text"
                class="h-12 w-12 mb-3 opacity-50"
              />
              <p class="text-sm">
                {{ t('LIBRARIES.SEARCH.EMPTY_STATE') }}
              </p>
              <p class="text-xs mt-1 opacity-75">
                {{ t('LIBRARIES.SEARCH.KEYBOARD_HELP') }}
              </p>
            </div>

            <!-- No results (only show after search has been performed) -->
            <div
              v-else-if="!hasResults && !isSearching && hasSearchedOnce"
              class="flex flex-col items-center justify-center py-8 text-slate-500 dark:text-slate-400"
            >
              <Icon icon="i-lucide-search" class="h-12 w-12 mb-3 opacity-50" />
              <p class="text-sm">
                {{ t('LIBRARIES.SEARCH.NO_RESULTS', { query: searchQuery }) }}
              </p>
              <p class="text-xs mt-1 opacity-75">
                {{ t('LIBRARIES.SEARCH.TRY_DIFFERENT') }}
              </p>
            </div>

            <!-- Search prompt when query exists but no search performed yet -->
            <div
              v-else-if="
                searchQuery.trim() &&
                !hasResults &&
                !isSearching &&
                !hasSearchedOnce
              "
              class="flex flex-col items-center justify-center py-8 text-slate-500 dark:text-slate-400"
            >
              <Icon icon="i-lucide-search" class="h-12 w-12 mb-3 opacity-50" />
              <p class="text-sm mb-4">
                {{
                  t('LIBRARIES.SEARCH.SEARCH_PROMPT', { query: searchQuery })
                }}
              </p>
              <Button
                :label="t('LIBRARIES.SEARCH.BUTTON')"
                icon="i-lucide-search"
                @click="performSearch(searchQuery)"
              />
            </div>

            <!-- Results list -->
            <div v-else-if="hasResults" ref="resultsRef" class="space-y-2">
              <div
                v-for="(result, index) in searchResults"
                :key="result.id || index"
                class="cursor-pointer rounded-lg border border-transparent p-3 transition-all hover:bg-slate-50 dark:hover:bg-slate-700"
                @click="selectDocument(result)"
              >
                <div class="flex items-start gap-3">
                  <!-- File icon -->
                  <div class="flex-shrink-0 mt-0.5">
                    <Icon
                      icon="i-lucide-file-text"
                      class="h-5 w-5 text-slate-400 dark:text-slate-500"
                    />
                  </div>

                  <!-- Content -->
                  <div class="flex-1 min-w-0">
                    <!-- Title with venue info -->
                    <h3
                      class="text-sm font-medium text-slate-900 dark:text-slate-100 truncate"
                    >
                      <span
                        v-if="result.metadata?.title"
                        v-html="
                          highlightSearchTerms(
                            result.metadata.title,
                            searchQuery
                          )
                        "
                      />
                      <span v-else class="text-slate-500">{{
                        t('LIBRARIES.SEARCH.UNTITLED')
                      }}</span>
                    </h3>

                    <!-- Content preview -->
                    <p
                      v-if="result.content"
                      class="mt-1 text-xs text-slate-600 dark:text-slate-400 line-clamp-2"
                      v-html="
                        highlightSearchTerms(
                          formatContent(result.content),
                          searchQuery
                        )
                      "
                    />

                    <!-- Metadata -->
                    <div
                      class="mt-2 flex items-center gap-2 text-xs text-slate-500"
                    >
                      <!-- Confidence Score -->
                      <span
                        v-if="result.metadata?.confidence"
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                        :class="{
                          'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200':
                            result.metadata.confidence > 0.7,
                          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200':
                            result.metadata.confidence > 0.4 &&
                            result.metadata.confidence <= 0.7,
                          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200':
                            result.metadata.confidence <= 0.4,
                        }"
                      >
                        {{
                          t('LIBRARIES.SEARCH.CONFIDENCE_MATCH', {
                            percentage: Math.round(
                              result.metadata.confidence * 100
                            ),
                          })
                        }}
                      </span>

                      <!-- Geo Match -->
                      <span
                        v-if="result.metadata?.geo_match"
                        class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                        :class="{
                          'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200':
                            result.metadata.geo_match === 'exact',
                          'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200':
                            result.metadata.geo_match === 'further',
                          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200':
                            result.metadata.geo_match === 'far',
                        }"
                      >
                        <Icon
                          :icon="
                            result.metadata.geo_match === 'exact'
                              ? 'i-lucide-map-pin-check'
                              : 'i-lucide-map-pin'
                          "
                          class="h-3 w-3"
                        />
                        {{
                          t(
                            `LIBRARIES.NEARBY.GEO_MATCH.${result.metadata.geo_match.toUpperCase()}`
                          )
                        }}
                      </span>

                      <!-- Distance -->
                      <span
                        v-if="result.metadata?._distance_meters != null"
                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300"
                      >
                        {{
                          t('LIBRARIES.NEARBY.DISTANCE', {
                            distance: Math.round(
                              result.metadata._distance_meters
                            ),
                          })
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer with keyboard shortcuts -->
          <div
            class="px-4 py-2 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-750 text-xs text-slate-500 dark:text-slate-400"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-4">
                <span class="flex items-center gap-1">
                  <kbd
                    class="px-1.5 py-0.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded text-xs"
                  >
                    {{ t('LIBRARIES.SEARCH.ESC_KEY') }}
                  </kbd>
                  {{ t('LIBRARIES.SEARCH.CLOSE') }}
                </span>
              </div>
              <span v-if="hasResults">
                {{ searchResults.length }}
                {{ t('LIBRARIES.SEARCH.RESULTS') }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
